import {
  ArrowDownToLine,
  Check,
  Download,
  Loader2,
  MousePointerClick,
  PenLine,
  Upload,
} from "lucide-react"
import { useCallback, useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PdfPageCanvas } from "@/components/PdfPageCanvas"
import { SignatureOverlay } from "@/components/SignatureOverlay"
import { SignatureProvider } from "@/components/SignatureProvider"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useDocumentUpload } from "@/hooks/useDocumentUpload"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { type PdfFile, usePdfThumbnails } from "@/hooks/usePdfThumbnails"
import { type SignaturePlacement, useSignPdf } from "@/hooks/useSignPdf"

const howItWorks = [
  { icon: Upload, text: "Upload PDF" },
  { icon: MousePointerClick, text: "Add & place signature" },
  { icon: ArrowDownToLine, text: "Download signed PDF" },
]

interface SavedSignature {
  id: number
  label: string | null
  url: string
}

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  signatures: SavedSignature[]
  authenticated: boolean
}

export default function Sign({ limits, signatures, authenticated }: Props) {
  const [pdfFile, setPdfFile] = useState<PdfFile | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [signatureSrc, setSignatureSrc] = useState<string | null>(null)
  const [signatureBytes, setSignatureBytes] = useState<Uint8Array | null>(null)
  const [placements, setPlacements] = useState<SignaturePlacement[]>([])
  const [pageDimensions, setPageDimensions] = useState<
    Record<number, { width: number; height: number }>
  >({})
  const [error, setError] = useState<string | null>(null)
  const [saveLabel, setSaveLabel] = useState("")
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [signatureAspectRatio, setSignatureAspectRatio] = useState(1)

  const { generateThumbnails, loading: loadingThumbnails } = usePdfThumbnails()
  const { signPdf, processing } = useSignPdf()
  const { uploadDocument } = useDocumentUpload()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const handleFilesAdded = async (files: File[]) => {
    const first = files[0]
    if (!first) return

    setRawFile(first)
    const file = await generateThumbnails(first)
    setPdfFile(file)
    setPlacements([])
    setError(null)
  }

  const handleSignatureReady = useCallback(
    (dataUrl: string, bytes: Uint8Array) => {
      setSignatureSrc(dataUrl)
      setSignatureBytes(bytes)

      const img = new Image()
      img.onload = () => {
        setSignatureAspectRatio(img.width / img.height)
      }
      img.src = dataUrl
    },
    [],
  )

  const handlePageClick = useCallback(
    (pageIndex: number) => {
      if (!signatureSrc) return
      const dims = pageDimensions[pageIndex]
      const sigWidth = 0.2
      const sigHeight = dims
        ? (sigWidth * dims.width) / (signatureAspectRatio * dims.height)
        : 0.05
      setPlacements((prev) => {
        if (prev.find((p) => p.pageIndex === pageIndex)) return prev
        return [
          ...prev,
          { pageIndex, x: 0.3, y: 0.7, width: sigWidth, height: sigHeight },
        ]
      })
    },
    [signatureSrc, pageDimensions, signatureAspectRatio],
  )

  const handlePlacementChange = useCallback(
    (
      pageIndex: number,
      update: { x: number; y: number; width: number; height: number },
    ) => {
      setPlacements((prev) =>
        prev.map((p) => (p.pageIndex === pageIndex ? { ...p, ...update } : p)),
      )
    },
    [],
  )

  const handleRemovePlacement = useCallback((pageIndex: number) => {
    setPlacements((prev) => prev.filter((p) => p.pageIndex !== pageIndex))
  }, [])

  const handleSign = async () => {
    if (!rawFile || !signatureBytes || placements.length === 0) return
    if (!guardOperation()) return

    setError(null)

    try {
      const blob = await signPdf(rawFile, signatureBytes, placements)

      const filename = `${rawFile.name.replace(".pdf", "")}_signed.pdf`
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)

      markCompleted()

      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content
      void fetch("/sign-pdf/track", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken ?? "" },
      })

      void uploadDocument(blob, filename, "sign_pdf")
    } catch {
      setError(
        "Failed to sign PDF. The file may be corrupted or password-protected.",
      )
    }
  }

  const handleSaveSignature = async () => {
    if (!signatureBytes) return

    setSaving(true)
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content

      const blob = new Blob([signatureBytes.buffer as ArrayBuffer], {
        type: "image/png",
      })
      const formData = new FormData()
      formData.append("image", blob, "signature.png")
      if (saveLabel) formData.append("label", saveLabel)

      const response = await fetch("/signatures", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken ?? "" },
        body: formData,
      })

      if (response.ok) {
        setSaved(true)
      } else {
        const data = (await response.json()) as { errors?: string[] }
        setError(data.errors?.join(", ") ?? "Failed to save signature.")
      }
    } catch {
      setError("Failed to save signature.")
    } finally {
      setSaving(false)
    }
  }

  const canSign =
    pdfFile && signatureBytes && placements.length > 0 && !processing

  return (
    <>
      <SEO
        title="Sign PDF Online Free | Add Digital Signature | StitchPDF"
        description="Add your signature to PDF documents for free. Upload a signature image, place it on any page with drag and drop. No software installation required."
        canonical="https://stitchpdf.com/sign-pdf"
        ogTitle="Sign PDF Online Free | StitchPDF"
        ogDescription="Add your signature to PDF documents for free online."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Sign PDF - StitchPDF",
            description:
              "Add your signature to PDF documents. Draw, type, or upload a signature image.",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Sign a PDF Online",
            description: "Add your signature to any PDF document for free.",
            step: [
              {
                "@type": "HowToStep",
                name: "Upload PDF",
                text: "Select the PDF document you want to sign.",
              },
              {
                "@type": "HowToStep",
                name: "Add signature",
                text: "Draw your signature, type it, or upload a signature image. Place it on any page.",
              },
              {
                "@type": "HowToStep",
                name: "Download signed PDF",
                text: "Click Sign & Download to save your signed document.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <PenLine className="text-primary h-8 w-8" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Sign PDF</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Add your signature to any page of your PDF
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasFile = pdfFile !== null
                const hasPlacement = placements.length > 0
                const isComplete =
                  completed ||
                  (hasFile && i === 0) ||
                  (hasPlacement && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasFile && !hasPlacement && i === 1) ||
                        (hasPlacement && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {!pdfFile && (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={(files) => void handleFilesAdded(files)}
                  multiple={false}
                />
              </div>
            )}

            {loadingThumbnails && (
              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading PDF...
              </div>
            )}

            {pdfFile && (
              <div className="mt-8">
                <div className="mb-6 flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {pdfFile.name} ({pdfFile.pageCount} pages)
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                <div className="mb-8">
                  <SignatureProvider
                    savedSignatures={signatures}
                    onSignatureReady={handleSignatureReady}
                  />
                </div>

                {signatureSrc && (
                  <>
                    <div className="mb-4 flex items-center gap-3">
                      <div className="rounded-lg border p-2">
                        <img
                          src={signatureSrc}
                          alt="Your signature"
                          className="h-10 w-auto object-contain"
                        />
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Click on a page below to place your signature
                      </p>
                    </div>

                    <div className="space-y-6">
                      {Array.from({ length: pdfFile.pageCount }, (_, i) => {
                        const placement = placements.find(
                          (p) => p.pageIndex === i,
                        )
                        const dims = pageDimensions[i]

                        return (
                          <div key={i} className="relative">
                            <p className="text-muted-foreground mb-1 text-xs">
                              Page {i + 1}
                            </p>
                            <div
                              onClick={() => handlePageClick(i)}
                              className="cursor-crosshair"
                            >
                              <PdfPageCanvas
                                file={rawFile!}
                                pageIndex={i}
                                onDimensionsReady={(d) =>
                                  setPageDimensions((prev) => ({
                                    ...prev,
                                    [i]: d,
                                  }))
                                }
                              >
                                {placement && dims && (
                                  <SignatureOverlay
                                    signatureSrc={signatureSrc}
                                    containerWidth={dims.width}
                                    containerHeight={dims.height}
                                    x={placement.x}
                                    y={placement.y}
                                    width={placement.width}
                                    height={placement.height}
                                    onChange={(update) =>
                                      handlePlacementChange(i, update)
                                    }
                                    onRemove={() => handleRemovePlacement(i)}
                                  />
                                )}
                              </PdfPageCanvas>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {error && (
                      <p className="text-destructive mt-4 text-sm">{error}</p>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <Button
                        onClick={() => void handleSign()}
                        disabled={!canSign}
                        className="gap-2"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Signing...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            Sign & Download
                          </>
                        )}
                      </Button>
                    </div>

                    {signatureBytes && authenticated && !saved && (
                      <div className="mt-6 rounded-lg border p-4">
                        <p className="text-sm font-medium">
                          Save this signature for future use?
                        </p>
                        <div className="mt-2 flex items-center gap-3">
                          <Input
                            placeholder="Label (optional)"
                            value={saveLabel}
                            onChange={(e) => setSaveLabel(e.target.value)}
                            className="max-w-xs"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleSaveSignature()}
                            disabled={saving}
                          >
                            {saving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {saved && (
                      <div className="mt-6 flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                        <Check className="h-4 w-4" />
                        Signature saved for future use
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <RelatedTools currentTool="sign-pdf" />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Draw your signature, type it, or upload an image. Click any
                  page to place it, then drag to position and resize. Download
                  the signed PDF when done.
                </p>
                <p>
                  Your document and signature stay in your browser. Nothing
                  uploads to our servers.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>Draw, type, or upload:</strong> Three ways to add
                    your signature
                  </li>
                  <li>
                    <strong>Click to place:</strong> Position on any page
                  </li>
                  <li>
                    <strong>Drag to resize:</strong> Adjust size and position
                  </li>
                  <li>
                    <strong>Save for later:</strong> Reuse signatures across
                    documents (with account)
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Sign contracts and agreements. Complete application forms.
                  Approve documents in business workflows. Add your signature to
                  letters.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
